import { EnvironmentClassOrderedList } from "@/components/EnvironmentClassOrderedList";
import {
    AuthenticationModal,
    type SCMAuthenticationModalProps,
} from "@/components/environments/onboarding/CreateProjectFromEnvironmentModal";
import { ExceptionErrorBoundary } from "@/components/ExceptionHandler";
import { Button } from "@/components/flexkit/Button";
import { Alert } from "@/components/podkit/alert/Alert";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogFooter,
    DialogHeader,
    DialogOverlay,
    DialogPortal,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { Input } from "@/components/podkit/forms/Input";
import { Label } from "@/components/podkit/forms/Label";
import { cn } from "@/components/podkit/lib/cn";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { type ParsedGitInfo } from "@/components/RepositoryInput";
import { RepositorySelector } from "@/components/RepositorySelector";
import {
    convertToProjectEnvironmentClass,
    useEnvironmentClassEntries,
    type EnvironmentClassEntry,
} from "@/hooks/use-environment-class-entries";
import { TrackLocations } from "@/hooks/use-segment";
import { useCreateProject, useUpdateProjectEnvironmentClasses } from "@/queries/project-queries";
import {
    useEnsureOnboardingRunner,
    useIsUserAuthenticatedWithRunner,
    useParseContextURL,
} from "@/queries/runner-queries";
import { EnvironmentCreateError } from "@/routes/environments/create/components/EnvironmentCreateError";
import { localEnvType } from "@/utils/environment-classes";
import { type GitConfig } from "@/utils/git-config";
import { getPreferredLocalEnvironmentClass } from "@/utils/test/api/environment-classes";
import { Code, ConnectError } from "@connectrpc/connect";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { getScmProviderName } from "frontend-shared/local-runner";
import { RunnerKind } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { ParseContextURLPreconditionFailureDetails } from "gitpod-next-api/gitpod/v1/runner_pb";
import { useCallback, useEffect, useMemo, useState, type FC, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDebounce } from "use-debounce";

export const CreateProjectModal: FC<{
    onClose: () => void;
    navigateOnCreate?: boolean;
    onProjectCreated?: (projectId: string) => void;
}> = ({ onClose, navigateOnCreate = true, onProjectCreated }) => {
    const navigate = useNavigate();
    const { toast } = useToast();

    // Repository input state
    const [repositoryUrl, setRepositoryUrl] = useState("");
    const [gitConfig, setGitConfig] = useState<GitConfig | null>(null);
    const [parsedInfo, setParsedInfo] = useState<ParsedGitInfo | null>(null);

    // State for parsing status
    const [isLoadingParseContext, setIsLoadingParseContext] = useState(false);
    const [isSuccessParseContext, setIsSuccessParseContext] = useState(false);
    const [parseContextFailed, setParseContextFailed] = useState(false);
    const [isNotFound, setIsNotFound] = useState(false);
    const [isInvalidArgument, setIsInvalidArgument] = useState(false);
    const [errorParseContext, setErrorParseContext] = useState<Error | null>(null);

    const { environmentClassEntries: unfilteredEnvironmentClassEntries, isLoading: isLoadingEnvironmentClassEntries } =
        useEnvironmentClassEntries();
    const [selectedEnvironmentClassEntries, setSelectedEnvironmentClassEntries] = useState<EnvironmentClassEntry[]>([]);

    const allEnvironmentClassEntries = useMemo(() => {
        const localRunner = unfilteredEnvironmentClassEntries.find((e) => e.localRunner);

        // filter out local runners of the current user
        const filtered = unfilteredEnvironmentClassEntries.filter((e) => !e.localRunner);

        // re-add a synthetic local runner
        if (localRunner) {
            localEnvType.policyRestricted = localRunner.policyRestricted;
            return [localEnvType, ...filtered];
        }
        return filtered;
    }, [unfilteredEnvironmentClassEntries]);

    const [projectName, setProjectName] = useState("");
    const [createError, setCreateError] = useState<Error | null>(null);
    const createProject = useCreateProject();
    const updateProjectEnvironmentClasses = useUpdateProjectEnvironmentClasses();

    const onOpenChange = useCallback(
        (open: boolean) => {
            if (!open) {
                onClose();
            }
        },
        [onClose],
    );

    // Use the first selected environment class for parsing context URL
    const firstSelectedEnvironmentClass: EnvironmentClassEntry | undefined = selectedEnvironmentClassEntries.length
        ? selectedEnvironmentClassEntries[0]
        : undefined;

    // Get a default runner for repository search when no environment class is selected
    const {
        data: defaultRunner,
        isLoading: isLoadingDefaultRunner,
        error: defaultRunnerError,
    } = useEnsureOnboardingRunner({ enabled: true });

    // Get the runner ID for parsing and repository search
    // Falls back to default runner if no environment class is selected yet
    const actualRunnerId = firstSelectedEnvironmentClass?.localRunner
        ? getPreferredLocalEnvironmentClass(unfilteredEnvironmentClassEntries)?.runner?.runnerId
        : (firstSelectedEnvironmentClass?.runner?.runnerId ?? defaultRunner?.runnerId);

    // Determine if we should auto-switch to manual mode
    // This happens when the defaultRunner query completed with an error (e.g., Enterprise org without runner managers)
    // and there's no selected environment class as an alternative
    const shouldAutoSwitchToManual = !isLoadingDefaultRunner && !!defaultRunnerError && !firstSelectedEnvironmentClass;

    // SCM host for repository search (auth handling is done in RepositorySelectorPopover)
    const scmHost = "github.com";

    // Debounce the repository URL to prevent too many API calls while typing
    const [debouncedRepoUrl] = useDebounce(repositoryUrl, 500);

    // Parse the repository URL (like onboarding)
    const {
        data: parsedContextUrl,
        isLoading: isParsing,
        error: parseError,
    } = useParseContextURL(actualRunnerId, debouncedRepoUrl || undefined, {
        enabled: !!debouncedRepoUrl && !!actualRunnerId,
    });

    // Update state based on parsing results
    useEffect(() => {
        setIsLoadingParseContext(isParsing);

        if (parseError) {
            setErrorParseContext(parseError);
            setIsSuccessParseContext(false);
            setGitConfig(null);
            setParsedInfo(null);

            // Check error types
            if (parseError instanceof ConnectError) {
                const details = parseError.findDetails(ParseContextURLPreconditionFailureDetails);
                if (details.length > 0) {
                    setParseContextFailed(true);
                    setIsNotFound(false);
                    setIsInvalidArgument(false);
                } else if (parseError.code === Code.NotFound) {
                    setIsNotFound(true);
                    setParseContextFailed(false);
                    setIsInvalidArgument(false);
                } else if (parseError.code === Code.InvalidArgument) {
                    setIsInvalidArgument(true);
                    setParseContextFailed(false);
                    setIsNotFound(false);
                } else {
                    setParseContextFailed(false);
                    setIsNotFound(false);
                    setIsInvalidArgument(false);
                }
            }
        } else if (parsedContextUrl?.git) {
            setErrorParseContext(null);
            setIsSuccessParseContext(true);
            setParseContextFailed(false);
            setIsNotFound(false);
            setIsInvalidArgument(false);

            // Update gitConfig and parsedInfo
            const info: ParsedGitInfo = {
                cloneUrl: parsedContextUrl.git.cloneUrl,
                branch: parsedContextUrl.git.branch,
                commit: parsedContextUrl.git.commit,
                repo: parsedContextUrl.git.repo,
                upstreamRemoteUrl: parsedContextUrl.git.upstreamRemoteUrl,
            };
            setParsedInfo(info);

            const config: GitConfig = {
                remoteUri: parsedContextUrl.git.cloneUrl,
                cloneTarget: parsedContextUrl.git.branch || parsedContextUrl.git.commit || "main",
                upstreamRemoteUri: parsedContextUrl.git.upstreamRemoteUrl || undefined,
                checkoutLocation: parsedContextUrl.git.repo || undefined,
            };
            setGitConfig(config);
        } else if (!debouncedRepoUrl) {
            // Reset state when URL is cleared
            setErrorParseContext(null);
            setIsSuccessParseContext(false);
            setParseContextFailed(false);
            setIsNotFound(false);
            setIsInvalidArgument(false);
            setGitConfig(null);
            setParsedInfo(null);
        }
    }, [parsedContextUrl, isParsing, parseError, debouncedRepoUrl]);

    // Authentication check for repository access
    const {
        data: authCheckResponse,
        error: errorAuthCheck,
        refetch: refetchAuthCheck,
    } = useIsUserAuthenticatedWithRunner(actualRunnerId, debouncedRepoUrl, {
        refetchUntilAuthenticated: true,
        enabled: !gitConfig,
    });

    // Handle errors
    const isAuthenticationRequired = parseContextFailed || authCheckResponse?.type === "AuthenticationRequired";

    // Get the SCM name from the error details
    const scmName = useMemo(() => {
        let scmID = "";
        if (isAuthenticationRequired) {
            if (parseContextFailed) {
                if (errorParseContext instanceof ConnectError) {
                    const details = errorParseContext.findDetails(ParseContextURLPreconditionFailureDetails);
                    for (const detail of details) {
                        if (detail.scmId) {
                            scmID = detail.scmId;
                        }
                    }
                }
            } else if (authCheckResponse?.type == "AuthenticationRequired") {
                return authCheckResponse.scmName;
            }
        }

        return getScmProviderName(scmID);
    }, [authCheckResponse, errorParseContext, isAuthenticationRequired, parseContextFailed]);

    // Calculate the SCM settings link
    const scmSettingsLink = useMemo(() => {
        // Use firstSelectedEnvironmentClass if available, otherwise fallback to defaultRunner
        // This matches the behavior of actualRunnerId
        const runner = firstSelectedEnvironmentClass?.runner ?? defaultRunner;

        if (!runner) {
            return undefined;
        }

        // Remote runners have their own settings page. Local runners are configured on an org level in the runner settings.
        return runner.kind === RunnerKind.REMOTE ? `/settings/runners/${runner.runnerId}` : "/settings/runners";
    }, [firstSelectedEnvironmentClass, defaultRunner]);

    // Set default project name from our parsed context
    useEffect(() => {
        if (!projectName && parsedInfo?.repo) {
            setProjectName(parsedInfo.repo);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps -- don't run on name change, because that would mean that it's not possible for the user to clear the input field
    }, [parsedInfo]);

    const [showScmAuthModal, setShowScmAuthModal] = useState<SCMAuthenticationModalProps | undefined>();

    const handleSubmit = useCallback(
        async (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();

            if (!projectName || !repositoryUrl || selectedEnvironmentClassEntries.length === 0) {
                return;
            }

            // Check if we have parsed context data
            if (!gitConfig && !parseContextFailed) {
                toast({
                    title: "Validating repository",
                    description: "Please wait while we validate the repository URL.",
                });
                return;
            }
            if (!gitConfig) {
                return;
            }

            try {
                setCreateError(null);
                const project = await createProject.mutateAsync({
                    name: projectName,
                    contextURL: repositoryUrl,
                    gitConfig: {
                        remoteUrl: gitConfig.remoteUri,
                        ref: gitConfig.cloneTarget,
                        upstreamRemoteUrl: gitConfig.upstreamRemoteUri,
                        checkoutLocation: gitConfig.checkoutLocation,
                    },
                });

                await updateProjectEnvironmentClasses.mutateAsync({
                    projectId: project.id,
                    projectEnvironmentClasses: selectedEnvironmentClassEntries.map((entry, index) =>
                        convertToProjectEnvironmentClass(entry, index),
                    ),
                });

                toast({
                    title: "Project created",
                    description: `Project "${projectName}" has been created successfully.`,
                });

                onProjectCreated?.(project.id);
                onClose();
                if (navigateOnCreate) {
                    navigate(`/projects/${project.id}`);
                }
            } catch (error) {
                setCreateError(error instanceof Error ? error : new Error(String(error)));
                toast({
                    title: "Failed to create project",
                    description: error instanceof Error ? error.message : "An unknown error occurred",
                });
            }
        },
        [
            projectName,
            repositoryUrl,
            selectedEnvironmentClassEntries,
            gitConfig,
            parseContextFailed,
            toast,
            createProject,
            updateProjectEnvironmentClasses,
            onProjectCreated,
            onClose,
            navigate,
            navigateOnCreate,
        ],
    );
    const handleAuthenticate = useCallback(
        (event: FormEvent<HTMLFormElement>) => {
            if (!firstSelectedEnvironmentClass || !debouncedRepoUrl) {
                return;
            }

            event.preventDefault();
            if (authCheckResponse?.type == "AuthenticationRequired") {
                setShowScmAuthModal({
                    repoURL: debouncedRepoUrl,
                    runnerId: firstSelectedEnvironmentClass.runner.runnerId,
                    authResponse: authCheckResponse,
                    onClose: () => setShowScmAuthModal(undefined),
                    onAuthSuccess: () => {
                        setShowScmAuthModal(undefined);
                        void refetchAuthCheck();
                    },
                    "data-track-location": TrackLocations.CreateProjectModal,
                });

                return;
            }
        },
        [firstSelectedEnvironmentClass, debouncedRepoUrl, authCheckResponse, refetchAuthCheck],
    );

    const showIsVerifying = !parseContextFailed && isLoadingParseContext && !!firstSelectedEnvironmentClass;
    const showAccessSettingsButton = isInvalidArgument && !!scmSettingsLink;

    // Form is valid if we have all required fields and either successful context parsing or auth failure
    const isFormValid =
        projectName &&
        debouncedRepoUrl &&
        selectedEnvironmentClassEntries.length > 0 &&
        (isSuccessParseContext || parseContextFailed) &&
        !isNotFound;

    const createButtonText = parseContextFailed ? "Authenticate" : "Create";

    return (
        <Dialog open onOpenChange={onOpenChange}>
            {/* we don't use DialogContent component because it uses css transform for centering and it breaks dnd behavior */}
            <ExceptionErrorBoundary>
                <DialogPortal>
                    <DialogOverlay />
                    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
                        <DialogPrimitive.Content
                            className={cn(
                                "max-h-[90%] w-[500px] max-w-full",
                                "flex flex-col overflow-x-auto rounded-xl border-0.5 border-border-base bg-surface-popover p-6 shadow-modal",
                                "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-top-[48%]",
                            )}
                            data-track-location={TrackLocations.CreateProjectModal}
                        >
                            <DialogHeader>
                                <DialogTitle>Create a project</DialogTitle>
                            </DialogHeader>

                            <DialogBody>
                                <form
                                    id="create-project-form"
                                    onSubmit={isAuthenticationRequired ? handleAuthenticate : handleSubmit}
                                    className="flex flex-col gap-4"
                                >
                                    <RepositorySelector
                                        label="Choose a repository"
                                        value={repositoryUrl}
                                        onChange={setRepositoryUrl}
                                        runnerId={actualRunnerId}
                                        scmHost={scmHost}
                                        trackingLocation="create-project-modal"
                                        autoSwitchToManual={shouldAutoSwitchToManual}
                                    />

                                    <div className="flex flex-col gap-2">
                                        <Label htmlFor="project-name" className="font-medium">
                                            Project name
                                        </Label>
                                        <Input
                                            id="project-name"
                                            data-testid="project-name-input"
                                            name="projectName"
                                            value={projectName}
                                            onChange={(e) => setProjectName(e.target.value)}
                                            placeholder="My Project"
                                            required={!isAuthenticationRequired} // if we need authentication, we don't want to require the project name to proceed
                                        />
                                    </div>

                                    <EnvironmentClassOrderedList
                                        label="Environment Classes"
                                        environmentClassEntries={selectedEnvironmentClassEntries}
                                        allEnvironmentClassEntries={allEnvironmentClassEntries}
                                        onChange={setSelectedEnvironmentClassEntries}
                                        loading={isLoadingEnvironmentClassEntries}
                                        disabled={!repositoryUrl}
                                        minItems={0}
                                        maxItems={30}
                                    />

                                    {selectedEnvironmentClassEntries.some((entry) => entry.localRunner) && ( // we check for repositoryUrlValid to only show the alert when the user is able to choose the environment class (see disabled prop on EnvironmentClassOrderedList)
                                        <Alert className="items-center gap-4 border-0 bg-surface-02">
                                            <span className="font-bold">Local runner selected. </span> <br />
                                            This project will be created with the{" "}
                                            <span className="font-bold">Ona Desktop</span> runner and will open with the
                                            corresponding local runner of each user.
                                        </Alert>
                                    )}

                                    <div
                                        className="flex min-h-4 flex-col items-center justify-center gap-2 text-sm"
                                        data-testid="errors"
                                    >
                                        {showIsVerifying && (
                                            <span data-testid="verifying">Verifying repository and connection...</span>
                                        )}

                                        {parseContextFailed && firstSelectedEnvironmentClass && (
                                            <span className="max-w-80 text-wrap">
                                                <span className="font-bold">
                                                    {firstSelectedEnvironmentClass?.runner?.name || "Unknown runner"}
                                                </span>{" "}
                                                requires authentication with{" "}
                                                <span className="font-bold">{scmName}</span>
                                            </span>
                                        )}
                                        {parseContextFailed && !firstSelectedEnvironmentClass && (
                                            <span className="max-w-80 text-wrap text-content-primary">
                                                Please select an environment class and authenticate to continue.
                                            </span>
                                        )}

                                        {isNotFound && (
                                            <span>
                                                The repository could not be found. Please check the URL and try again.
                                            </span>
                                        )}

                                        {!showIsVerifying &&
                                            !parseContextFailed &&
                                            !isNotFound &&
                                            (errorParseContext || errorAuthCheck) && (
                                                // fallback case is just for type safety
                                                <EnvironmentCreateError
                                                    error={
                                                        errorParseContext ??
                                                        errorAuthCheck ??
                                                        new Error("Unknown error")
                                                    }
                                                />
                                            )}

                                        {createError && (
                                            <span className="text-sm">
                                                {createError instanceof ConnectError
                                                    ? createError.rawMessage
                                                    : createError.toString()}
                                            </span>
                                        )}
                                    </div>
                                </form>
                            </DialogBody>

                            <DialogFooter>
                                <DialogClose asChild>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={onClose}
                                        data-tracking-id="cancel-create-project-modal"
                                    >
                                        Cancel
                                    </Button>
                                </DialogClose>

                                {showAccessSettingsButton && (
                                    <Button variant="outline" type="button" asChild>
                                        <Link
                                            to={scmSettingsLink ?? ""}
                                            onClick={onClose}
                                            data-testid="open-repository-settings-link"
                                            data-tracking-id="open-repository-access-settings-create-project-modal"
                                        >
                                            Open repository access settings
                                        </Link>
                                    </Button>
                                )}

                                {!isAuthenticationRequired ? (
                                    <Button
                                        type="submit"
                                        form="create-project-form"
                                        variant="primary"
                                        disabled={
                                            !isFormValid ||
                                            createProject.isPending ||
                                            updateProjectEnvironmentClasses.isPending ||
                                            showIsVerifying
                                        }
                                        loading={createProject.isPending}
                                        className={cn(showAccessSettingsButton && "hidden")}
                                        data-tracking-id="create-project-create-project-modal"
                                    >
                                        {createButtonText}
                                    </Button>
                                ) : (
                                    <Button
                                        type="submit"
                                        form="create-project-form"
                                        variant="primary"
                                        className={cn(showAccessSettingsButton && "hidden")}
                                        data-tracking-id="authenticate-scm-create-project-modal"
                                        disabled={!firstSelectedEnvironmentClass}
                                    >
                                        Authenticate
                                    </Button>
                                )}
                            </DialogFooter>
                            {showScmAuthModal && <AuthenticationModal {...showScmAuthModal} />}
                        </DialogPrimitive.Content>
                    </div>
                </DialogPortal>
            </ExceptionErrorBoundary>
        </Dialog>
    );
};
